import { useEffect, useMemo, useState } from "react";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import Card from "../../components/shared/Card/Card";
import EbayMessagingConversationList from "../../components/ebayMessaging/EbayMessagingConversationList";
import EbayMessagingStats from "../../components/ebayMessaging/EbayMessagingStats";
import EbayMessagingUserPerformance from "../../components/ebayMessaging/EbayMessagingUserPerformance";
import LoadingWheel from "../../components/shared/LoadingWheel";
import classNames from "../../utils/classNames";
import useEbayMessages from "../../data/useEbayMessages";
import useEbayMessagingStats from "../../data/useEbayMessagingStats";
import { IEbayMessage } from "../../models/EbayMessage";
import { SAMPLE_EBAY_MESSAGING_STATS } from "../../components/ebayMessaging/sampleMessagingStats";
import { useSignalR } from "../../contexts/SignalRContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";

type FilterMode = "all" | "unread" | "awaiting" | "flagged";

const DAY_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "1 day" },
  { value: 7, label: "1 week" },
  { value: 14, label: "2 weeks" },
  { value: 30, label: "1 month" },
];

const isFlagged = (message: IEbayMessage): boolean => {
  const flagged = message.flagged;
  if (!flagged) return false;
  return (
    flagged === true ||
    flagged === 1 ||
    String(flagged).toLowerCase() === "true" ||
    String(flagged) === "1"
  );
};

const isRead = (message: IEbayMessage): boolean =>
  String(message.read).toLowerCase() === "true";

/**
 * Omni-search across the fields most useful for locating a message:
 * - eBay username (`sendingUserID`)
 * - Listing / SKU ids (`itemID`)
 * - Listing title — covers MPN and part type keywords (`itemTitle`)
 * - Message body (`body` / `text`)
 * - Subject line (`subject`)
 */
const matchesSearch = (message: IEbayMessage, term: string): boolean => {
  if (!term) return true;
  const needle = term.toLowerCase();
  const haystack = [
    message.sendingUserID,
    message.sendToName,
    message.itemID,
    message.itemTitle,
    message.subject,
    message.body,
    message.text,
  ]
    .filter(Boolean)
    .join(" \u0001 ")
    .toLowerCase();
  return haystack.includes(needle);
};

export default function EbayMessages() {
  const { setInfo } = usePageNameContext();
  const { activeMessages } = useSignalR();
  const [page, setPage] = useState(1);
  const [days, setDays] = useState(7);
  const [filter, setFilter] = useState<FilterMode>("all");
  const [searchTerm, setSearchTerm] = useState("");

  const { unread, allmsgs } = useEbayMessages({ page, days });
  const { messagingStats } = useEbayMessagingStats({ daysToSearch: days });

  useEffect(() => {
    setInfo({
      name: "eBay Messages",
      desc: "Monitor, reply to and escalate customer eBay messages.",
    });
  }, []);

  const source = filter === "unread" ? unread : allmsgs;
  const rawMessages = source.data?.data;

  const filteredMessages = useMemo(() => {
    if (!rawMessages) return undefined;
    const trimmed = searchTerm.trim();
    return rawMessages.filter((message) => {
      if (!matchesSearch(message, trimmed)) return false;
      switch (filter) {
        case "awaiting":
          return !isRead(message);
        case "flagged":
          return isFlagged(message);
        default:
          return true;
      }
    });
  }, [rawMessages, filter, searchTerm]);

  // TODO(ebay-messages): drop the sample fallback once
  // GET /ebaymessaging/stats ships (see docs/pending-endpoints.md).
  const realStats = messagingStats.data?.data;
  const stats = realStats ?? SAMPLE_EBAY_MESSAGING_STATS;
  const usingSampleStats = !realStats;

  const toggleFilter = (next: FilterMode) => {
    setFilter((current) => (current === next ? "all" : next));
    setPage(1);
  };

  const handleDaysChange = (nextDays: number) => {
    setDays(nextDays);
    setPage(1);
  };

  const filterButton = (mode: FilterMode, label: string) => (
    <button
      type="button"
      key={mode}
      onClick={() => toggleFilter(mode)}
      className={classNames(
        "rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors",
        filter === mode
          ? "bg-primary-600 text-white ring-primary-600"
          : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
      )}
    >
      {label}
    </button>
  );

  const resultCount = filteredMessages?.length ?? 0;

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-slate-700">
            Window
          </label>
          <select
            value={days}
            onChange={(e) => handleDaysChange(parseInt(e.target.value))}
            className="rounded-md border-slate-300 text-sm focus:border-primary-500 focus:ring-primary-500"
          >
            {DAY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {filterButton("all", "All")}
          {filterButton("unread", "Unread")}
          {filterButton("awaiting", "Awaiting response")}
          {filterButton("flagged", "Flagged")}
        </div>
      </div>

      <EbayMessagingStats
        stats={stats}
        daysToSearch={days}
        awaitingActive={filter === "awaiting"}
        onToggleAwaiting={() => toggleFilter("awaiting")}
        flaggedActive={filter === "flagged"}
        onToggleFlagged={() => toggleFilter("flagged")}
      />

      {usingSampleStats && (
        <p className="-mt-2 text-xs text-slate-500">
          Sample numbers shown while the messaging stats endpoint is
          pending — see{" "}
          <code className="rounded bg-slate-100 px-1">
            docs/pending-endpoints.md
          </code>
          .
        </p>
      )}

      <Card
        title="Reply performance"
        subtitle="Replies sent and average reply time per user in the selected window."
        headerRight={<UserGroupIcon className="h-5 w-5 text-slate-400" />}
        collapsible
        defaultOpen
        bodyClassName="px-4 py-4 sm:px-6"
      >
        <EbayMessagingUserPerformance
          userStats={stats.userStats}
          isLoading={messagingStats.isLoading}
          isSample={usingSampleStats}
        />
      </Card>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="relative flex-1 min-w-[260px] max-w-xl">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              placeholder="Search eBay username, SKU / item ID, MPN, part type…"
              className="block w-full rounded-md border border-slate-300 py-2 pl-9 pr-9 text-sm text-slate-900 placeholder-slate-400 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                aria-label="Clear search"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {searchTerm && (
            <p className="text-xs text-slate-500">
              {resultCount.toLocaleString("en-GB")} match
              {resultCount === 1 ? "" : "es"} on page {page}
            </p>
          )}
        </div>

        {source.isLoading && !rawMessages ? (
          <div className="rounded-lg bg-white shadow-sm ring-1 ring-inset ring-slate-200 p-8">
            <LoadingWheel />
          </div>
        ) : (
          <EbayMessagingConversationList
            messages={filteredMessages}
            activeViewers={activeMessages}
          />
        )}

        <div className="flex items-center justify-center gap-3 text-slate-700">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm font-medium shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            <span className="sr-only">Previous</span>
          </button>
          <span className="text-sm font-medium">Page {page}</span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm font-medium shadow-sm hover:bg-slate-50"
          >
            <ChevronRightIcon className="h-4 w-4" />
            <span className="sr-only">Next</span>
          </button>
        </div>
      </div>
    </div>
  );
}
